/**
 * Admin access tiers:
 *   Super Admins  – full access to every admin feature.
 *   Solution Admins – access only to features listed in SOLUTION_ADMIN_FEATURES.
 *   Simulator Validation users – the CS team: the Pricing Simulator screens plus
 *     Config Admin, restricted to Excel validation (no reset / upload-to-DB).
 *
 * Eventually replace with admin role from fetch-scope API.
 */

// ---------------------------------------------------------------------------
// Email lists
// ---------------------------------------------------------------------------

export const SUPER_ADMIN_EMAILS: string[] = [
    'vijay.shankar@asper.ai',
    'praveen.s2@asper.ai',
    'suman.kumar@asper.ai',
    'raghav.dhir@asper.ai',
    'akshat.upadhyay@asper.ai',
    'pratyush.kumar@asper.ai',
    'sudeepa.shettyks@asper.ai',
    'arun.kashyap@asper.ai',
    'teja.rayala@asper.ai',
    'samruddha.shah@asper.ai',
    'prakhar.chitransh@asper.ai',
    'sneha.chaurasia@asper.ai',
    'rohit.mishra@asper.ai',
    'promo@asper.ai',
];

export const SOLUTION_ADMIN_EMAILS: string[] = [
    // Add solution-admin emails here; they get feature-gated access.
    'anand.rnaik@asper.ai',
    'varuni.ashtaputre@asper.ai',
    'asif.anas@asper.ai',
    'rajat.kumar2@asper.ai',
    'hussain.sameja@asper.ai',
];

export const SIMULATOR_VALIDATION_EMAILS: string[] = [
    // The CS team. They see the Pricing Simulator screens and Config Admin, and
    // are limited to Excel validation (reset & upload-to-DB are hidden for them).
    'pradeep.m@asper.ai',
    'pavan.r@asper.ai',
    'maharsh.prajapati@asper.ai',
    'jitesh.mittal@asper.ai',
    'rushikesh.khandetod@asper.ai',
    'sunil.ranganathan@asper.ai',
    'nitin.saraswat@asper.ai',
    'sunil.chib@asper.ai',
];

// ---------------------------------------------------------------------------
// Feature keys & permission mapping
// ---------------------------------------------------------------------------

export type AdminFeatureKey =
    | 'redis-management'
    | 'force-refresh'
    | 'features'
    | 'user-management'
    | 'migration-history'
    | 'de-refresh-jobs'
    | 'simulator-validation'
    | 'ppa-simulator'
    | 'ppa-config-validation'
    | 'config-admin'
    | 'app-lightup'
    | 'client-branding';

/** Features that Solution Admins are allowed to see / navigate to. */
export const SOLUTION_ADMIN_FEATURES: AdminFeatureKey[] = [
    // Enable features for solution admins by adding keys here, e.g.:
    'redis-management',
    'force-refresh',
    'app-lightup',
    'client-branding',
];

/**
 * Features that Simulator Validation users are allowed to see / navigate to.
 *
 * This is the CS tier. Config Admin is here rather than on Solution Admin
 * because it is the point of the tool: CS edits the config tables directly,
 * while the reset and upload-to-DB actions stay out of reach.
 */
export const SIMULATOR_VALIDATION_FEATURES: AdminFeatureKey[] = ['simulator-validation', 'config-admin'];

/** Maps each admin sub-route to its feature key for route-level guarding. */
export const ADMIN_ROUTE_FEATURE_MAP: Record<string, AdminFeatureKey> = {
    '/admin/cache-management': 'redis-management',
    '/admin/force-refresh': 'force-refresh',
    '/admin/migration-history': 'migration-history',
    '/admin/de-refresh-jobs': 'de-refresh-jobs',
    '/admin/simulator-validation': 'simulator-validation',
    '/admin/ppa-simulator': 'ppa-simulator',
    '/admin/ppa-config-validation': 'ppa-config-validation',
    '/admin/config': 'config-admin',
    '/admin/applightup': 'app-lightup',
    '/admin/client-branding': 'client-branding',
};

/**
 * Feature key for a path, matching nested routes too.
 *
 * Config Admin routes carry the feature and table as path segments
 * (/admin/config/:feature/:table), so an exact-match lookup would leave every
 * page below the index unguarded.
 */
export function resolveAdminFeature(pathname: string): AdminFeatureKey | undefined {
    const exact = ADMIN_ROUTE_FEATURE_MAP[pathname];
    if (exact) return exact;

    const prefix = Object.keys(ADMIN_ROUTE_FEATURE_MAP).find((route) => pathname.startsWith(`${route}/`));
    return prefix ? ADMIN_ROUTE_FEATURE_MAP[prefix] : undefined;
}

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

function getEmail(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('email') : null;
}

export function isSuperAdmin(): boolean {
    const email = getEmail();
    return !!email && SUPER_ADMIN_EMAILS.includes(email);
}

export function isSolutionAdmin(): boolean {
    const email = getEmail();
    return !!email && SOLUTION_ADMIN_EMAILS.includes(email);
}

export function isSimulatorValidationUser(): boolean {
    const email = getEmail();
    return !!email && SIMULATOR_VALIDATION_EMAILS.includes(email);
}

/** Backward-compatible: returns true for any admin tier. */
export function isAdminUser(): boolean {
    return isSuperAdmin() || isSolutionAdmin() || isSimulatorValidationUser();
}

/** Super Admins can access everything; other tiers only their allowed features. */
export function canAccessAdminFeature(key: AdminFeatureKey): boolean {
    if (isSuperAdmin()) return true;
    if (isSolutionAdmin() && SOLUTION_ADMIN_FEATURES.includes(key)) return true;
    if (isSimulatorValidationUser() && SIMULATOR_VALIDATION_FEATURES.includes(key)) return true;
    return false;
}

/**
 * Whether the current user may perform write/destructive simulator actions
 * (Reset All Tables, Upload to DB). Simulator Validation users are limited to
 * Excel validation only; Super Admins keep full access.
 */
export function canManageSimulatorData(): boolean {
    if (isSuperAdmin()) return true;
    return !isSimulatorValidationUser();
}
